import React from 'react';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { useField } from 'formik';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { cn } from '../../utils/cn';

interface SelectOption {
  value?: string | number;
  id?: string | number;
  title?: string;
  label?: string;
}

interface SelectFormFieldProps {
  name: string;
  label?: string;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
  data?: SelectOption[];
  className?: string;
  required?: boolean;
  onValueChange?: (value: string) => void;
}

export const SelectFormField: React.FC<SelectFormFieldProps> = ({
  name,
  label,
  disabled,
  loading,
  placeholder = 'Vyberte...',
  data,
  className,
  required,
  onValueChange: onValueChangeProp,
}) => {
  const [field, meta, helpers] = useField(name);
  const hasError = meta.touched && !!meta.error;
  const errorMsg = hasError ? meta.error : null;

  const handleValueChange = (value: string) => {
    helpers.setValue(value);
    helpers.setTouched(true);
    onValueChangeProp?.(value);
  };

  const renderItems =
    data && data.length > 0
      ? data.map((item, i) => {
          const value = item.value || item.id || String(i);
          const title = item.title || item.label || String(value);
          return (
            <SelectItem
              key={i}
              data-testid={`select-form-field-${name}-option-${value}`}
              value={String(value)}
            >
              {title}
            </SelectItem>
          );
        })
      : [];

  const selectValue = field.value ? String(field.value) : undefined;

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {label && (
        <label htmlFor={name} className="text-sm font-medium text-neutral-700">
          {label}
          {required && <span className="text-destructive ml-0.5">*</span>}
        </label>
      )}
      <Select
        name={field.name}
        value={selectValue}
        onValueChange={handleValueChange}
        disabled={disabled || loading}
      >
        <SelectTrigger
          data-testid={`select-form-field-${name}`}
          className={cn('w-full', hasError && 'border-destructive aria-invalid:border-destructive')}
          aria-invalid={hasError}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="z-[10001]" position="popper">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <SpinnerOverlay size="sm" />
            </div>
          ) : renderItems.length > 0 ? (
            renderItems
          ) : (
            <div className="px-2 py-1.5 text-sm text-neutral-500">Žádné možnosti</div>
          )}
        </SelectContent>
      </Select>
      {hasError && <span className="text-destructive text-xs">{errorMsg}</span>}
    </div>
  );
};
